import { useApolloClient } from '@apollo/client';
import { FlashbarProps, SelectProps } from '@cloudscape-design/components';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Modal from '@cloudscape-design/components/modal';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import React, { useCallback, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import {
  FoodMenue,
  GetWeeklyFoodMenuesDocument,
  GetWeeklyFoodMenuesQuery,
  useUpdateWeeklyFoodMenueMutation,
} from '../../../../generated/graphql';
import { FormValues, schema } from './Create';
import Form, { dayOptions } from './Form';
import { createDDBItem } from './lib/Helper';

type EditProps = {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  selectedId?: string | null;
  notifyDeletedOrUpdated: (resource: FlashbarProps.MessageDefinition) => void;
  notifyInProgress: (resource?: FlashbarProps.MessageDefinition) => void;
  notifyFailed: (failedResource: FlashbarProps.MessageDefinition) => void;
  additiveOptions: SelectProps.Option[];
  allergenOptions: SelectProps.Option[];
};

export default function Edit({
  visible,
  selectedId,
  setVisible,
  notifyInProgress,
  notifyDeletedOrUpdated,
  notifyFailed,
  additiveOptions,
  allergenOptions,
}: EditProps) {
  const params = useParams();
  const client = useApolloClient();
  const [updateWeeklyFoodMenue, { loading: updateLoading }] =
    useUpdateWeeklyFoodMenueMutation();
  const methods = useForm<FormValues>({
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (selectedId && selectedId.length > 0) {
      const items = client.readQuery({
        query: GetWeeklyFoodMenuesDocument,
        variables: {
          Filter: {
            week: {
              eq: Number(params.week),
            },
          },
        },
      }) as GetWeeklyFoodMenuesQuery;
      if (
        items &&
        items.listWeeklyFoodMenues &&
        items.listWeeklyFoodMenues.items &&
        items.listWeeklyFoodMenues.items.length > 0
      ) {
        const item = items.listWeeklyFoodMenues.items.filter(
          (item) => item?.id === selectedId,
        )[0];
        if (item) {
          const productInfos = item.productinformation
            ? (item.productinformation.map((item) => ({
                value: item?.value,
                label: item?.label,
              })) as SelectProps.Option[])
            : undefined;
          const displayday = item.displayday ?? 0;
          methods.reset({
            title: item.title ?? '',
            subtitle: item.subtitle ?? '',
            price:
              new Intl.NumberFormat('de-DE', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(Number(item.price)) ?? '',
            foodtype: (item.foodtype as SelectProps.Option) ?? undefined,
            displayday: dayOptions.filter(
              (option) => Number(option.value) === displayday,
            )[0],
            is_active: item.is_active ?? false,
            productinformation: productInfos,
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, methods.reset, selectedId]);

  const onSubmit = (data: any) => {
    if (selectedId && selectedId.length > 0) {
      const entry = createDDBItem(data);
      const messageId = uuidv4();
      notifyInProgress({ id: messageId, header: 'Eintrag wird bearbeitet...' });
      updateWeeklyFoodMenue({
        variables: {
          input: { id: selectedId, ...entry, week: Number(params.week ?? 0) },
        },
        update: (cache, { data }) => {
          const addData = data?.updateFoodMenue;
          const existingData = cache.readQuery({
            query: GetWeeklyFoodMenuesDocument,
            variables: {
              Filter: {
                week: {
                  eq: Number(params.week),
                },
              },
            },
          }) as GetWeeklyFoodMenuesQuery;
          const oldData = existingData.listWeeklyFoodMenues
            ?.items as FoodMenue[];
          cache.writeQuery({
            query: GetWeeklyFoodMenuesDocument,
            variables: {
              Filter: {
                week: {
                  eq: Number(params.week),
                },
              },
            },
            data: {
              listWeeklyFoodMenues: {
                items: oldData.map((item) => {
                  if (item.id === selectedId) {
                    return { ...addData };
                  } else {
                    return {
                      ...item,
                    };
                  }
                }),
              },
            },
          });
        },
      })
        .then((data) => {
          const result = data.data?.updateFoodMenue;
          if (result) {
            notifyInProgress();
            notifyDeletedOrUpdated({
              id: messageId,
              type: 'success',
              content: `Eintrag mit dem Namen "${result.title}" wurde bearbeitet.`,
              header: 'Gespeichert',
              dismissible: true,
            });
            setVisible(false);
          }
        })
        .catch((error) => {
          notifyInProgress();
          notifyFailed({
            id: messageId,
            content: JSON.stringify(error),
            header: 'Ein Fehler ist aufgetreten',
            dismissible: true,
          });
        });
    }
  };

  const handleCancel = useCallback(() => {
    setVisible(false);
    methods.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methods.reset, setVisible]);

  return (
    <FormProvider {...methods}>
      <Modal
        onDismiss={handleCancel}
        visible={visible}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={handleCancel}>
                Abbrechen
              </Button>
              <Button
                variant="primary"
                disabled={
                  isEmpty(methods.formState.dirtyFields) || updateLoading
                }
                formAction="submit"
                onClick={() => methods.handleSubmit(onSubmit)()}
              >
                Speichern
              </Button>
            </SpaceBetween>
          </Box>
        }
        header={'Eintrag bearbeiten'}
      >
        <Form
          additiveOptions={additiveOptions}
          allergenOptions={allergenOptions}
        />
      </Modal>
    </FormProvider>
  );
}
